"use client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./StudentList.css";
import AdminAuthModal from "../AdminAuthModal/AdminAuthModal";
import { useAdminAuth } from "../AdminAuthModal/useAdminAuth";

const API_BASE_URL = "https://apione.zorvixetechnologies.in";

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 🔥 FIXED: Added course_selection to form data
  const [formData, setFormData] = useState({
    student_id: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    student_type: "zorvixe_core",
    course_selection: "web_development", // 🔥 FIXED: Added with default
    role: "student", // 🔥 NEW: Added role with default
    batch_month: "",
    batch_year: "",
    password: "",
    status: "active",
  });

  // 🔥 Course options
  // In StudentList.js - around line 62
const courseOptions = [
  { value: "web_development", label: "Web Development" },
  { value: "digital_marketing", label: "Digital Marketing" },
  { value: "java_programming", label: "Java Programming" },
];

  const [editingStudent, setEditingStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Student type options
  const studentTypes = [
    { value: "zorvixe_core", label: "Zorvixe Core" },
    { value: "zorvixe_pro", label: "Zorvixe Pro" },
    { value: "zorvixe_elite", label: "Zorvixe Elite" },
  ];

  // 🔥 NEW: Role options
  const roleOptions = [
    { value: "student", label: "Student" },
    { value: "admin", label: "Admin" },
  ];

  // 🔥 FIXED: Filters with courseSelection
  const [filters, setFilters] = useState({
    search: "",
    batchMonth: "",
    batchYear: "",
    status: "",
    studentType: "",
    courseSelection: "", // 🔥 FIXED: Added course filter
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Online stats
  const [onlineStats, setOnlineStats] = useState({
    totalOnline: 0,
    totalStudents: 0,
  });

  // Admin Auth
  const {
    isAuthenticated,
    showAuthModal,
    loading: authLoading,
    setShowAuthModal,
    handleAuthSuccess,
    getAuthHeaders,
  } = useAdminAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchStudents();
      const interval = setInterval(fetchStudents, 30000);
      return () => clearInterval(interval);
    }
  }, [filters, pagination.page, isAuthenticated]);

  // 🔥 FIXED: Updated fetchStudents with proper API URL and course filter
  const fetchStudents = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add all filters
      if (filters.search) params.append("search", filters.search);
      if (filters.batchMonth) params.append("batchMonth", filters.batchMonth);
      if (filters.batchYear) params.append("batchYear", filters.batchYear);
      if (filters.status) params.append("status", filters.status);
      if (filters.studentType) params.append("studentType", filters.studentType);
      if (filters.courseSelection) params.append("courseSelection", filters.courseSelection); // 🔥 FIXED: Add course filter
      
      // Pagination
      params.append("page", pagination.page);
      params.append("limit", pagination.limit);

      console.log("📡 Fetching students with params:", params.toString());

      const response = await fetch(
        `${API_BASE_URL}/api/admin/students?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setStudents(result.data.students || []);
        setOnlineStats(
          result.data.onlineStats || { totalOnline: 0, totalStudents: 0 }
        );
        setPagination(
          result.data.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            pages: 0,
          }
        );
        console.log(`✅ Fetched ${result.data.students?.length || 0} students`);
      } else {
        throw new Error(result.message || "Failed to fetch students");
      }
    } catch (err) {
      console.error("❌ Error fetching students:", err);
      toast.error(err.message || "Failed to fetch students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Online status indicator component
  const OnlineStatus = ({ isOnline }) => (
    <div
      className={`online-status-stud ${
        isOnline ? "online-stud" : "offline-stud"
      }`}
    >
      <div className="status-dot-stud"></div>
      <span>{isOnline ? "Online" : "Offline"}</span>
    </div>
  );

  // Password display component
  const PasswordDisplay = ({ password }) => {
    const [visible, setVisible] = useState(false);

    return (
      <div className="password-display-stud">
        <span>{visible ? password : "••••••••"}</span>
        <button
          type="button"
          className="btn-password-toggle-stud"
          onClick={() => setVisible(!visible)}
          title={visible ? "Hide password" : "Show password"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            {visible ? (
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
            ) : (
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            )}
          </svg>
        </button>
      </div>
    );
  };

  // 🔥 FIXED: Get student type badge
  const getStudentTypeBadge = (type) => {
    const typeConfig = {
      zorvixe_core: { color: "#4a6bff", bg: "#e8edff", label: "CORE" },
      zorvixe_pro: { color: "#10b981", bg: "#ecfdf5", label: "PRO" },
      zorvixe_elite: { color: "#f59e0b", bg: "#fffbeb", label: "ELITE" },
    };
    const config = typeConfig[type] || typeConfig.zorvixe_core;
    return (
      <span
        className="student-type-badge-stud"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
        }}
      >
        {config.label}
      </span>
    );
  };

  const validCourses = ["web_development", "digital_marketing", "java_programming"];

  // 🔥 NEW: Get course badge
  const getCourseBadge = (course) => {
    const courseConfig = {
      web_development: { color: "#0d9488", bg: "#f0fdfa", label: "Web Dev" },
      digital_marketing: { color: "#b45309", bg: "#fff7ed", label: "Digi Mkt" },
      java_programming: { color: "#ea580c", bg: "#fff7ed", label: "Java" },
    };
    const config = courseConfig[course] || courseConfig.web_development;
    return (
      <span
        className="course-badge-stud"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600'
        }}
      >
        {config.label}
      </span>
    );
  };

  // 🔥 NEW: Get role badge
  const getRoleBadge = (role) => {
    const isAdmin = role === 'admin';
    return (
      <span
        className="course-badge-stud"
        style={{
          backgroundColor: isAdmin ? '#fef2f2' : '#f0fdfa',
          color: isAdmin ? '#ef4444' : '#0d9488',
          border: `1px solid ${isAdmin ? '#ef4444' : '#0d9488'}20`,
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600'
        }}
      >
        {isAdmin ? 'ADMIN' : 'STUDENT'}
      </span>
    );
  };

  // 🔥 FIXED: Handle form submit with course_selection and role
// In StudentList.js - Update handleSubmit function around line 280

const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const url = editingStudent
      ? `${API_BASE_URL}/api/admin/students/${editingStudent.id}`
      : `${API_BASE_URL}/api/admin/students`;

    const method = editingStudent ? "PUT" : "POST";

    // Prepare the request body
    const submitData = { ...formData };

    // For editing, handle password and updatePassword flag
    if (editingStudent) {
      // If password is empty, remove it from the request
      if (!submitData.password || submitData.password.trim() === "") {
        delete submitData.password;
      } else {
        // Password provided – add the required flag
        submitData.updatePassword = true;
      }
    }

    // Ensure course_selection and role are always set
    submitData.course_selection = submitData.course_selection || "web_development";
    submitData.role = submitData.role || "student";

    console.log("📤 Sending data to server:", {
      url,
      method,
      hasPassword: !!submitData.password,
      updatePasswordFlag: submitData.updatePassword,
      course_selection: submitData.course_selection,
      role: submitData.role,
    });

    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(submitData),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      toast.success(
        editingStudent
          ? "Student updated successfully"
          : "Student created successfully"
      );
      closeModal();
      setFormData({
        student_id: "",
        email: "",
        first_name: "",
        last_name: "",
        phone: "",
        student_type: "zorvixe_core",
        course_selection: "web_development",
        role: "student",
        batch_month: "",
        batch_year: "",
        password: "",
        status: "active",
      });
      setEditingStudent(null);
      fetchStudents();
    } else {
      throw new Error(
        result.message || result.error || "Failed to save student"
      );
    }
  } catch (error) {
    console.error("❌ Error saving student:", error);
    toast.error(error.message || "Failed to save student");
  }
};

  const showCreateModal = () => {
    setEditingStudent(null);
    setFormData({
      student_id: "",
      email: "",
      first_name: "",
      last_name: "",
      phone: "",
      student_type: "zorvixe_core",
      course_selection: "web_development",
      role: "student",
      batch_month: "",
      batch_year: "",
      password: "",
      status: "active",
    });
    setIsModalOpen(true);
  };

  const createstudent = () => {
    navigate("/student_register");
  };

  // 🔥 FIXED: Handle edit with course_selection and role
  // In StudentList.js - Update handleEdit function

const handleEdit = (student) => {
  console.log("✏️ Editing student with data:", student);
  
  // Log the course_selection value specifically
  console.log("📚 Student course_selection value:", student.course_selection);
  console.log("📚 Student role value:", student.role);
  
  setEditingStudent(student);
  setFormData({
    student_id: student.student_id,
    email: student.email,
    first_name: student.first_name,
    last_name: student.last_name,
    phone: student.phone || "",
    student_type: student.student_type || "zorvixe_core",
    course_selection: student.course_selection || "web_development", // 🔥 FIXED
    role: student.role || "student", // 🔥 NEW
    batch_month: student.batch_month || "",
    batch_year: student.batch_year || "",
    password: "",
    status: student.status || "active",
  });
  setIsModalOpen(true);
};

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStudent(null);
    setShowPassword(false);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?"))
      return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/students/${studentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success("Student deleted successfully");
        fetchStudents();
      } else {
        throw new Error(
          result.message || result.error || "Failed to delete student"
        );
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error(error.message || "Failed to delete student");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      batchMonth: "",
      batchYear: "",
      status: "",
      studentType: "",
      courseSelection: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "#10B981", bg: "#ECFDF5", label: "ACTIVE" },
      inactive: { color: "#EF4444", bg: "#FEF2F2", label: "INACTIVE" },
      pending: { color: "#F59E0B", bg: "#FFFBEB", label: "PENDING" },
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span
        className="status-badge-stud"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
        }}
      >
        {config.label}
      </span>
    );
  };

  const getBatchInfo = (batch_month, batch_year) => {
    if (!batch_month && !batch_year) return "All Batches";
    if (batch_month && batch_year) return `${batch_month} ${batch_year}`;
    if (batch_month) return batch_month;
    if (batch_year) return batch_year;
    return "All Batches";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getDisplayName = (student) => {
    return (
      student.fullName ||
      `${student.first_name || ""} ${student.last_name || ""}`.trim() ||
      "Unknown Student"
    );
  };

  // Student type statistics
  const getStudentTypeStats = () => {
    const stats = {
      zorvixe_core: 0,
      zorvixe_pro: 0,
      zorvixe_elite: 0,
    };
    students.forEach((student) => {
      const type = student.student_type || "zorvixe_core";
      if (stats[type] !== undefined) stats[type]++;
    });
    return stats;
  };

  // 🔥 NEW: Course statistics
 const getCourseStats = () => {
  const stats = {
    web_development: 0,
    digital_marketing: 0,
    java_programming: 0,
  };
  students.forEach((student) => {
    const course = student.course_selection || "web_development";
    if (stats[course] !== undefined) stats[course]++;
  });
  return stats;
};

  // Pagination component
  const Pagination = () => {
    const { page, pages } = pagination;
    if (pages <= 1) return null;

    return (
      <div className="pagination-stud">
        <button
          className="pagination-btn-stud"
          onClick={() => handlePageChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </button>
        <div className="pagination-pages-stud">
          {Array.from({ length: pages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === pages || (p >= page - 1 && p <= page + 1)
            )
            .map((p, index, array) => {
              const showEllipsis = index > 0 && p - array[index - 1] > 1;
              return (
                <div key={p} style={{ display: "flex", alignItems: "center" }}>
                  {showEllipsis && (
                    <span className="pagination-ellipsis-stud">...</span>
                  )}
                  <button
                    className={`pagination-page-stud ${
                      p === page ? "active-stud" : ""
                    }`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                </div>
              );
            })}
        </div>
        <button
          className="pagination-btn-stud"
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= pages}
        >
          Next
        </button>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="pa-loading-container-stud">
        <div className="pa-loader-stud"></div>
        <p>Checking authorization...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    );
  }

  const studentTypeStats = getStudentTypeStats();
  const courseStats = getCourseStats();

  return (
    <div className="student-management-admin-stud">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div className="admin-header-stud">
        <div className="header-content-stud">
          <h1>Student Management</h1>
          <p>Manage and monitor all student accounts</p>
          {/* Online Stats */}
          <div className="online-stats-stud">
            <div className="stat-item-stud">
              <span className="stat-number-stud">{onlineStats.totalOnline}</span>
              <span className="stat-label-stud">Online Now</span>
            </div>
            <div className="stat-item-stud">
              <span className="stat-number-stud">{onlineStats.totalStudents}</span>
              <span className="stat-label-stud">Total Students</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="btn-logout-stud"
            onClick={() => {
              localStorage.removeItem("adminAuth");
              localStorage.removeItem("adminToken");
              window.location.reload();
            }}
          >
            Logout Admin
          </button>
          <button className="btn-create-stud" onClick={createstudent}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add New Student
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="pa-loading-container-stud">
          <div className="pa-loader-stud"></div>
          <p>Loading students...</p>
        </div>
      )}

      {/* Stats Overview */}
      <div className="stats-overview-stud">
        <div className="stat-card-stud">
          <div className="stat-icon-stud total-stud">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A2.01 2.01 0 0018.06 7h-1.12c-.94 0-1.81.56-2.19 1.42L13.5 13.5 16 18v4h4zm-7.5-10.5c.56 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zM9.5 6c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm3.5 6c-1.11 0-2 .89-2 2s.89 2 2 2 2-.89 2-2-.89-2-2-2zm-7.5 6c-1.11 0-2 .89-2 2s.89 2 2 2 2-.89 2-2-.89-2-2-2zm0-2c-1.11 0-2 .89-2 2s.89 2 2 2 2-.89 2-2-.89-2-2-2z" />
            </svg>
          </div>
          <div className="stat-info-stud">
            <h3>{pagination.total}</h3>
            <p>Total Students</p>
          </div>
        </div>
        <div className="stat-card-stud">
          <div className="stat-icon-stud active-stud">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <div className="stat-info-stud">
            <h3>{students.filter((s) => s.status === "active").length}</h3>
            <p>Active Students</p>
          </div>
        </div>
        <div className="stat-card-stud">
          <div className="stat-icon-stud" style={{ backgroundColor: "#e8edff" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#4a6bff">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            </svg>
          </div>
          <div className="stat-info-stud">
            <h3>{studentTypeStats.zorvixe_core}</h3>
            <p>Zorvixe Core</p>
          </div>
        </div>
        <div className="stat-card-stud">
          <div className="stat-icon-stud" style={{ backgroundColor: "#ecfdf5" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#10b981">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            </svg>
          </div>
          <div className="stat-info-stud">
            <h3>{studentTypeStats.zorvixe_pro}</h3>
            <p>Zorvixe Pro</p>
          </div>
        </div>
        <div className="stat-card-stud">
          <div className="stat-icon-stud" style={{ backgroundColor: "#fffbeb" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#f59e0b">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            </svg>
          </div>
          <div className="stat-info-stud">
            <h3>{studentTypeStats.zorvixe_elite}</h3>
            <p>Zorvixe Elite</p>
          </div>
        </div>
      </div>

      {/* 🔥 NEW: Course Stats Row */}
      <div className="stats-overview-stud" style={{ marginTop: '20px' }}>
  <div className="stat-card-stud">
    <div className="stat-icon-stud" style={{ backgroundColor: "#f0fdfa" }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="#0d9488">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14h-2v-6h2v6zm0-8h-2V6h2v2z" />
      </svg>
    </div>
    <div className="stat-info-stud">
      <h3>{courseStats.web_development}</h3>
      <p>Web Development</p>
    </div>
  </div>
  <div className="stat-card-stud">
    <div className="stat-icon-stud" style={{ backgroundColor: "#fff7ed" }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="#b45309">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 2h6v2h-6V6zm0 4h6v2h-6v-2zm0 4h6v2h-6v-2zM8 6v2H4V6h4zm0 4v2H4v-2h4zm0 4v2H4v-2h4z" />
      </svg>
    </div>
    <div className="stat-info-stud">
      <h3>{courseStats.digital_marketing}</h3>
      <p>Digital Marketing</p>
    </div>
  </div>
  {/* 🔥 NEW: Java Programming Stat Card */}
  <div className="stat-card-stud">
    <div className="stat-icon-stud" style={{ backgroundColor: "#fff7ed" }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="#ea580c">
        <path d="M18 9v4H6V9H4v6h16V9h-2z" />
      </svg>
    </div>
    <div className="stat-info-stud">
      <h3>{courseStats.java_programming}</h3>
      <p>Java Programming</p>
    </div>
  </div>
</div>

      {/* Filters Section */}
      <div className="filters-section-stud">
        <div className="filters-header-stud">
          <h3>Filters</h3>
          <button className="btn-clear-stud" onClick={clearFilters}>
            Clear All
          </button>
        </div>
        <div className="filters-grid-stud">
          <div className="filter-group-stud">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name, email, or student ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </div>
          <div className="filter-group-stud">
            <label>Batch Month</label>
            <select
              value={filters.batchMonth}
              onChange={(e) => handleFilterChange("batchMonth", e.target.value)}
            >
              <option value="">All Months</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>
          <div className="filter-group-stud">
            <label>Batch Year</label>
            <select
              value={filters.batchYear}
              onChange={(e) => handleFilterChange("batchYear", e.target.value)}
            >
              <option value="">All Years</option>
              <option value="2023">2023</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
            </select>
          </div>
          <div className="filter-group-stud">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group-stud">
            <label>Student Type</label>
            <select
              value={filters.studentType}
              onChange={(e) => handleFilterChange("studentType", e.target.value)}
            >
              <option value="">All Types</option>
              {studentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          {/* 🔥 FIXED: Course filter */}
          <div className="filter-group-stud">
            <label>Course</label>
            <select
              value={filters.courseSelection}
              onChange={(e) =>
                handleFilterChange("courseSelection", e.target.value)
              }
            >
              <option value="">All Courses</option>
              {courseOptions.map((course) => (
                <option key={course.value} value={course.value}>
                  {course.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-content-stud">
        <div className="content-header-stud">
          <h2>Student Directory</h2>
          <span className="student-count-stud">
            Showing {students.length} of {pagination.total} students
            {pagination.pages > 1 &&
              ` (Page ${pagination.page} of ${pagination.pages})`}
          </span>
        </div>

        {/* Students Table */}
        {!loading && students.length === 0 ? (
          <div className="empty-state-stud">
            <div className="empty-icon-stud">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3>No students found</h3>
            <p>Try adjusting your filters or add new students</p>
            <button className="btn-create-stud" onClick={showCreateModal}>
              Add New Student
            </button>
          </div>
        ) : (
          <div className="students-table-container-stud">
            <table className="students-table-stud">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>Course</th> {/* 🔥 FIXED: Course column */}
                  <th>Role</th> {/* 🔥 NEW: Role column */}
                  <th>Batch</th>
                  <th>Password</th>
                  <th>Status</th>
                  <th>Online</th>
                  <th>Join Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div className="avatar-container-stud">
                        <div className="student-avatar-stud">
                          {student.profile_image ? (
                            <img
                              src={student.profile_image}
                              alt={getDisplayName(student)}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <span
                            style={{
                              display: student.profile_image ? "none" : "flex",
                            }}
                          >
                            {getDisplayName(student).charAt(0).toUpperCase()}
                          </span>
                          <div
                            className={`online-badge-stud ${
                              student.is_online ? "online-stud" : "offline-stud"
                            }`}
                          ></div>
                        </div>
                        <div>
                          <div className="student-name-stud">
                            {getDisplayName(student)}
                          </div>
                          <div className="student-id-stud">
                            {student.student_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "12px" }}>
                          {student.email}
                        </div>
                        <div style={{ color: "#6b7280", fontSize: "12px" }}>
                          {student.phone || "Not provided"}
                        </div>
                      </div>
                    </td>
                    <td>
                      {getStudentTypeBadge(student.student_type || "zorvixe_core")}
                    </td>
                    <td>
                      {getCourseBadge(student.course_selection || "web_development")}
                    </td>
                    <td>
                      {getRoleBadge(student.role || "student")}
                    </td>
                    <td style={{ color: "#6b7280", fontSize: "12px" }}>
                      {getBatchInfo(student.batch_month, student.batch_year)}
                    </td>
                    <td style={{ color: "#6b7280", fontSize: "12px" }}>
                      <PasswordDisplay password={student.password} />
                    </td>
                    <td>{getStatusBadge(student.status)}</td>
                    <td>
                      <OnlineStatus isOnline={student.is_online} />
                    </td>
                    <td style={{ color: "#6b7280", fontSize: "12px" }}>
                      {formatDate(student.join_date || student.created_at)}
                    </td>
                    <td>
                      <div className="student-actions-stud">
                        <button
                          className="btn-action-stud edit-stud"
                          onClick={() => handleEdit(student)}
                          title="Edit student"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                          </svg>
                        </button>
                        <button
                          className="btn-action-stud delete-stud"
                          onClick={() => handleDelete(student.id)}
                          title="Delete student"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bottom */}
        {pagination.pages > 1 && <Pagination />}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay-stud" onClick={closeModal}>
          <div
            className="modal-content-stud"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-stud">
              <h2>{editingStudent ? "Edit Student" : "Add New Student"}</h2>
              <button className="modal-close-stud" onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form-stud">
              <div className="form-grid-stud">
                <div className="form-group-stud">
                  <label htmlFor="student-id" className="form-label-stud">
                    Student ID *
                  </label>
                  <input
                    type="text"
                    className="form-input-stud"
                    id="student-id"
                    value={formData.student_id}
                    onChange={(e) =>
                      setFormData({ ...formData, student_id: e.target.value })
                    }
                    required
                    placeholder="Enter student ID"
                  />
                </div>

                <div className="form-group-stud">
                  <label htmlFor="student-email" className="form-label-stud">
                    Email *
                  </label>
                  <input
                    type="email"
                    className="form-input-stud"
                    id="student-email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group-stud">
                  <label htmlFor="first-name" className="form-label-stud">
                    First Name *
                  </label>
                  <input
                    type="text"
                    className="form-input-stud"
                    id="first-name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    required
                    placeholder="Enter first name"
                  />
                </div>

                <div className="form-group-stud">
                  <label htmlFor="last-name" className="form-label-stud">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    className="form-input-stud"
                    id="last-name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    required
                    placeholder="Enter last name"
                  />
                </div>

                <div className="form-group-stud">
                  <label htmlFor="student-phone" className="form-label-stud">
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="form-input-stud"
                    id="student-phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="Enter phone number"
                  />
                </div>

                {/* 🔥 NEW: Role Selection */}
                <div className="form-group-stud">
                  <label htmlFor="role" className="form-label-stud">
                    Role *
                  </label>
                  <select
                    className="form-input-stud"
                    id="role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                  >
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 🔥 FIXED: Course Selection Field */}
                <div className="form-group-stud">
                  <label htmlFor="course-selection" className="form-label-stud">
                    Course *
                  </label>
                  <select
                    className="form-input-stud"
                    id="course-selection"
                    value={formData.course_selection}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        course_selection: e.target.value,
                      })
                    }
                    required
                  >
                    {courseOptions.map((course) => (
                      <option key={course.value} value={course.value}>
                        {course.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group-stud">
                  <label htmlFor="student-type" className="form-label-stud">
                    Student Type *
                  </label>
                  <select
                    className="form-input-stud"
                    id="student-type"
                    value={formData.student_type}
                    onChange={(e) =>
                      setFormData({ ...formData, student_type: e.target.value })
                    }
                    required
                  >
                    {studentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group-stud password-field-stud">
                  <label htmlFor="password" className="form-label-stud">
                    Password {editingStudent && "(Leave blank to keep current)"}
                  </label>
                  <div className="password-input-container-stud">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-input-stud"
                      id="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder={
                        editingStudent ? "Enter new password" : "Enter password"
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle-stud"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        {showPassword ? (
                          <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                        ) : (
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="form-group-stud">
                  <label htmlFor="student-status" className="form-label-stud">
                    Status *
                  </label>
                  <select
                    className="form-input-stud"
                    id="student-status"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="form-group-stud">
                  <label htmlFor="batch-month" className="form-label-stud">
                    Batch Month
                  </label>
                  <select
                    className="form-input-stud"
                    id="batch-month"
                    value={formData.batch_month}
                    onChange={(e) =>
                      setFormData({ ...formData, batch_month: e.target.value })
                    }
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                </div>

                <div className="form-group-stud">
                  <label htmlFor="batch-year" className="form-label-stud">
                    Batch Year
                  </label>
                  <select
                    className="form-input-stud"
                    id="batch-year"
                    value={formData.batch_year}
                    onChange={(e) =>
                      setFormData({ ...formData, batch_year: e.target.value })
                    }
                  >
                    <option value="">Select Year</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </select>
                </div>
              </div>

              <div className="form-actions-stud">
                <button type="button" className="btn-cancel-stud" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit-stud">
                  {editingStudent ? "Update Student" : "Create Student"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;